import { SearchComponent } from "@/App/Tenants/Search";
import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useEffect, useState } from "react";
import { ApiDataProps } from "../Tenants";
import { TypePageActions } from "../Plans";
import { AddBtn } from "@/App/Contests/Add";
import { TableData } from "@/App/Contests/Table";
import { getContestssData, searchContestsData } from "@/Api/contests";
import { getSelectProductGroups } from "@/Api/productGroups";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { Trophy } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MultiSelectCheckbox } from "@/components/ui/multi-select-checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
export type TableDataProps = {
  id: number;
  uuid: string;
  name: string;
  validation_date: string;
  lottery_id: number;
  prize: number;
  is_active: boolean;
  contest_status_id: number;
  tenant_id: number;
  has_investigation: number;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  contest_status: {
    id: number;
    name: string;
  };
  lottery: {
    id: number;
    name: string;
    color: string;
  };
  tenant: {
    id: number;
    name: string;
  };
  creator?: {
    id: number;
    name: string;
  } | null;
  product_classifications: {
    id: number;
    contest_id: number;
    lottery_id: number;
    product_name: string;
    product_classification_id: number;
    tenant_id: number;
    created_at: string;
    updated_at: string;
    lottery: {
      id: number;
      name: string;
    };
    product_classification: {
      id: number;
      uuid: string;
      lottery_id: number;
      name: string;
      game_quantity: number;
      dez_quantity: number;
      quota_quantity: number;
      total_without_tariff: number;
      total_with_tariff: number;
      game_percentage: number;
      tariff: number;
      sale_price: number;
      price_without_tariff: number;
      commission_by_quote: number;
      image: string | null;
      is_active: number;
      product_type_id: number;
      tenant_id: number;
      created_at: string;
      updated_at: string;
      deleted_at: string | null;
    };
  }[];
};

export const PageList = () => {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");
  const { hasPermission } = usePermissions();

  const [lotteries, setLotteries] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedLotteries, setSelectedLotteries] = useState<string[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [validationStartDate, setValidationStartDate] = useState<string>("");
  const [validationEndDate, setValidationEndDate] = useState<string>("");

  const fetchData = async (
    page: number = 1,
    lotteryIds: string[] = [],
    active: string = "all",
    startDate: string = "",
    endDate: string = ""
  ) => {
    setState("loading");
    const { data } = await getContestssData(page, {
      lottery_id: lotteryIds.length > 0 ? lotteryIds : undefined,
      is_active: active === "all" ? undefined : Number(active),
      validation_start_date: startDate || undefined,
      validation_end_date: endDate || undefined,
    });
    setListData(data?.response);
    setState("production");
  };

  useEffect(() => {
    fetchData();
    (async () => {
      const { data, ok } = await getSelectProductGroups();
      if (ok && data?.response) {
        setLotteries(data.response);
      }
    })();
  }, []);

  const pageActions: TypePageActions = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        await fetchData(
          listData?.current_page + 1,
          selectedLotteries,
          activeFilter,
          validationStartDate,
          validationEndDate
        );
      }
      return;
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        await fetchData(
          listData?.current_page - 1,
          selectedLotteries,
          activeFilter,
          validationStartDate,
          validationEndDate
        );
      }
      return;
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        await fetchData(
          listData?.last_page,
          selectedLotteries,
          activeFilter,
          validationStartDate,
          validationEndDate
        );
      }
      return;
    },
  };

  const handleApplyFilter = () => {
    fetchData(1, selectedLotteries, activeFilter, validationStartDate, validationEndDate);
  };

  const handleClearFilter = () => {
    setSelectedLotteries([]);
    setActiveFilter("all");
    setValidationStartDate("");
    setValidationEndDate("");
    fetchData(1, [], "all", "", "");
  };

  return (
    <Main>
      <If condition={hasPermission("view-contest-list")}>
        <PageHeader
          icon={Trophy}
          title="Concursos"
          description="Gerencie os concursos ativos"
        >
          <If condition={hasPermission("create-contest")}>
            <AddBtn updateFront={setListData} dataApi={listData} />
          </If>
        </PageHeader>
        <div className="flex flex-col gap-3">
          <div className="bg-white dark:bg-[#2c2c2c] p-4 rounded-lg border flex flex-col gap-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="space-y-2">
                <Label>Pesquisar</Label>
                <SearchComponent
                  updateFront={setListData}
                  setState={setState}
                  funcSearch={searchContestsData}
                  placeholder="Pesquisar concursos"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="active-filter">Ativo</Label>
                <Select value={activeFilter} onValueChange={setActiveFilter}>
                  <SelectTrigger id="active-filter">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="1">Ativo</SelectItem>
                    <SelectItem value="0">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="validation_start_date">Validade Início</Label>
                <Input
                  id="validation_start_date"
                  type="date"
                  value={validationStartDate}
                  onChange={(e) => setValidationStartDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="validation_end_date">Validade Fim</Label>
                <Input
                  id="validation_end_date"
                  type="date"
                  value={validationEndDate}
                  onChange={(e) => setValidationEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="lottery-filter">Loteria</Label>
                <MultiSelectCheckbox
                  options={lotteries.map((lottery) => ({
                    value: lottery.id.toString(),
                    label: lottery.name,
                  }))}
                  selectedValues={selectedLotteries}
                  onSelectionChange={setSelectedLotteries}
                  placeholder="Selecione a loteria"
                  selectAllLabel="SELECIONAR TUDO"
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={handleApplyFilter} className={cn(colors.bg_primary, "flex-1")}>
                  Aplicar Filtros
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClearFilter}
                  className="flex-1 bg-background text-foreground border-input hover:bg-accent hover:text-foreground"
                >
                  Limpar
                </Button>
              </div>
            </div>
          </div>

          {state === "loading" ? (
            <CreateLoaderTable cols={4} lines={6} />
          ) : (
            <TableData
              dataApi={listData}
              pageActions={pageActions}
              updateFront={setListData}
            />
          )}
        </div>
      </If>
    </Main>
  );
};
